import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Trash2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { EntShell, Btn, Kicker, StatusBadge, errText, inputCls } from "@/components/enterprise/Ent";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STAT_LABELS = [
  ["joined", "Participants joined"], ["teams_formed", "Teams formed"], ["team_decisions", "Team decisions"],
  ["teams_correct", "Best-supported conclusions"], ["pre_assessments", "Pre-event reflections"], ["post_assessments", "Post-event reflections"],
];

const Stats = ({ stats, cap }) => (
  <dl className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#ddd5c9] border border-[#ddd5c9] rounded-lg overflow-hidden" data-testid="event-stats">
    {STAT_LABELS.map(([k, label]) => (
      <div key={k} className="bg-white p-5">
        <dt className="text-xs text-[#5a524b]">{label}</dt>
        <dd className="font-display text-3xl mt-1" data-testid={`event-stat-${k}`}>{stats[k]}{k === "joined" ? <span className="text-base text-[#5a524b]"> / {cap}</span> : null}</dd>
      </div>
    ))}
  </dl>
);

const Notes = ({ eventId, initial }) => {
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/enterprise/events/${eventId}/notes`, { observations: text }, { withCredentials: true }); toast.success("Observations saved."); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <section className="bg-white border border-[#ddd5c9] rounded-lg p-6" aria-labelledby="notes-h">
      <h2 id="notes-h" className="font-display text-2xl">Facilitator observations</h2>
      <p className="text-sm text-[#5a524b] mt-1">Optional. These appear in the report, clearly labeled as facilitator observations. Don't name individuals.</p>
      <label htmlFor="notes" className="sr-only">Facilitator observations</label>
      <textarea id="notes" rows={4} maxLength={4000} className={`${inputCls} mt-4`} value={text} onChange={(e) => setText(e.target.value)} data-testid="event-notes-input" />
      <Btn variant="dark" className="mt-3" onClick={save} disabled={busy} data-testid="event-notes-save">Save observations</Btn>
    </section>
  );
};

const Guide = ({ guide }) => (
  <section className="bg-white border border-[#ddd5c9] rounded-lg p-6" aria-labelledby="guide-h" data-testid="event-guide">
    <h2 id="guide-h" className="font-display text-2xl">Facilitator guide</h2>
    <Accordion type="multiple" className="mt-3">
      {guide.map((s, i) => (
        <AccordionItem key={s.heading} value={`g${i}`} className="border-[#efe9e0]">
          <AccordionTrigger className="text-left font-semibold" data-testid={`event-guide-section-${i}`}>{s.heading}</AccordionTrigger>
          <AccordionContent>
            <ul className="list-disc pl-5 space-y-1.5 text-[15px] text-[#3b3530]">{s.items.map((it) => <li key={it}>{it}</li>)}</ul>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const StateNotice = ({ status }) => {
  const msg = {
    pending_payment: "Payment hasn't been completed for this booking. If you paid, refresh in a minute. Otherwise, book again from your dashboard.",
    checkout_error: "Checkout couldn't start for this booking. Please book again.",
    in_review: "Our team is reviewing your customization. We'll email you when your event is confirmed, and the join link will work from then.",
  }[status];
  return msg ? <p role="status" className="bg-[#fbf3e3] border border-[#e9d3a6] rounded-md p-4 text-[15px]" data-testid="event-state-notice">{msg}</p> : null;
};

export default function EnterpriseEvent() {
  const { eventId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try { const r = await api.get(`/enterprise/events/${eventId}`, { withCredentials: true }); setD(r.data); }
    catch (e) { setErr(e?.response?.status === 401 ? "Please sign in to view this event." : errText(e, "Event not found.")); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.event?.status !== "live") return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [d?.event?.status, load]);

  const setState = async (status) => {
    try { await api.post(`/enterprise/events/${eventId}/state`, { status }, { withCredentials: true }); toast.success(status === "live" ? "Event is live." : "Event completed. Solutions are now revealed to participants."); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const purge = async () => {
    try { await api.delete(`/enterprise/events/${eventId}/participant-data`, { withCredentials: true }); toast.success("Participant data deleted."); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  if (err) return <EntShell><div className="max-w-5xl mx-auto px-6 py-20"><p role="alert" data-testid="event-error">{err}</p><Link to="/enterprise/login" className="underline">Sign in</Link></div></EntShell>;
  if (!d) return <EntShell><div className="max-w-5xl mx-auto px-6 py-20" aria-busy="true">Loading…</div></EntShell>;
  const ev = d.event;
  const joinUrl = `${window.location.origin}/e/${ev.event_code}`;
  const ready = ["scheduled", "live", "completed"].includes(ev.status);

  return (
    <EntShell>
      <div className="max-w-5xl mx-auto px-6 py-14 space-y-8">
        <div>
          <Link to="/enterprise/dashboard" className="text-sm underline" data-testid="event-back">← Dashboard</Link>
          <div className="mt-4 flex items-center gap-3 flex-wrap"><Kicker>{d.org?.name}{d.org?.department ? ` · ${d.org.department}` : ""}</Kicker><StatusBadge status={ev.status} testid="event-status" /></div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3" data-testid="event-title">{ev.program.name}</h1>
          <p className="mt-2 text-[15px] text-[#3b3530]">{ev.capacity?.label}: {ev.capacity?.people} · {ev.option === "ready" ? "Ready-to-Play" : "Customized"} · Reflections {ev.assessments_enabled ? "on" : "off"}</p>
        </div>
        <StateNotice status={ev.status} />

        {ready && (
          <section className="bg-[#16130f] text-white rounded-lg p-6 grid md:grid-cols-[1fr_auto] gap-6 items-center" aria-label="Participant access">
            <div>
              <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#e9c98a]">Participant access</div>
              <div className="font-display text-5xl tracking-[0.2em] mt-2" data-testid="event-code">{ev.event_code}</div>
              <div className="text-sm text-[#d9d2c8] mt-1 break-all" data-testid="event-join-url">{joinUrl}</div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Btn onClick={() => { navigator.clipboard.writeText(joinUrl); toast.success("Join link copied."); }} data-testid="event-copy-link"><Copy size={16} aria-hidden="true" /> Copy join link</Btn>
              {ev.status === "scheduled" && <Btn variant="outline" className="!text-white !border-white hover:!bg-white hover:!text-[#16130f]" onClick={() => setState("live")} data-testid="event-start">Start event</Btn>}
              {["scheduled", "live"].includes(ev.status) && <Btn variant="outline" className="!text-white !border-white hover:!bg-white hover:!text-[#16130f]" onClick={() => setState("completed")} data-testid="event-complete">Mark complete and reveal</Btn>}
            </div>
          </section>
        )}

        {ready && <Stats stats={d.stats} cap={ev.participant_cap} />}

        <div className="flex flex-wrap gap-3">
          <a href={`${API}/enterprise/events/${eventId}/guide.pdf`} className="ent-btn inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-md border border-[#16130f] hover:bg-[#16130f] hover:text-white" data-testid="event-guide-pdf"><Download size={16} aria-hidden="true" /> Facilitator guide (PDF)</a>
          {ready && <a href={`${API}/enterprise/events/${eventId}/report.pdf`} className="ent-btn inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-md bg-[#7a1010] text-white hover:bg-[#5e0c0c]" data-testid="event-report-pdf"><Download size={16} aria-hidden="true" /> Team Impact Report (PDF){ev.status !== "completed" ? " (interim)" : ""}</a>}
        </div>

        {ready && <Notes eventId={eventId} initial={d.facilitator_notes} />}
        <Guide guide={d.guide} />

        {ready && (
          <section className="border border-[#e3c9c9] bg-[#fbf6f4] rounded-lg p-6" aria-labelledby="privacy-h">
            <h2 id="privacy-h" className="font-display text-2xl">Participant data</h2>
            <p className="text-sm text-[#3b3530] mt-1">Nicknames, reflections, and team decisions are deleted automatically 12 months after the event. You can delete them now. Download your report first, because it will no longer include this data.</p>
            {ev.data_purged ? <p className="mt-3 text-sm font-semibold" data-testid="event-purged">Participant data was deleted on {ev.data_purged_at?.slice(0, 10)}.</p> : (
              <AlertDialog>
                <AlertDialogTrigger asChild><Btn variant="outline" className="mt-4 !border-[#7a1010] !text-[#7a1010] hover:!bg-[#7a1010] hover:!text-white" data-testid="event-purge-open"><Trash2 size={16} aria-hidden="true" /> Delete participant data</Btn></AlertDialogTrigger>
                <AlertDialogContent className="bg-white text-[#16130f]">
                  <AlertDialogHeader><AlertDialogTitle>Delete all participant data for this event?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#3b3530]">This permanently removes nicknames, reflections, and team decisions. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel data-testid="event-purge-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={purge} className="bg-[#7a1010] text-white" data-testid="event-purge-confirm">Delete permanently</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </section>
        )}
      </div>
    </EntShell>
  );
}
